'use client';

import type { FeedbackInvite } from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';

import {
  FeedbackInviteStatusBadge,
  FeedbackModeLabel,
} from '@/core/components/feedback/feedback-bits';

type InviteRowCopy = {
  recipients: string;
  recipientsOne: string;
  progress: string;
};

type FeedbackInviteListRowProps = {
  invite: FeedbackInvite;
  locationName: string;
  dateLabel: string;
  copy: InviteRowCopy;
  onOpen: () => void;
  className?: string;
};

export function formatInviteRecipientLabel(
  invite: Pick<FeedbackInvite, 'recipientCount' | 'completedCount'>,
  copy: InviteRowCopy,
): string {
  const total = invite.recipientCount ?? 0;
  const completed = invite.completedCount ?? 0;

  if (total > 0 && completed > 0) {
    return copy.progress
      .replace('{completed}', String(completed))
      .replace('{total}', String(total));
  }
  if (total === 1) {
    return copy.recipientsOne;
  }
  return copy.recipients.replace('{count}', String(total));
}

/**
 * Status-board invite row — location + recipients carry scan when titles collide.
 */
export function FeedbackInviteListRow({
  invite,
  locationName,
  dateLabel,
  copy,
  onOpen,
  className,
}: FeedbackInviteListRowProps) {
  const context = invite.contextLabel?.trim();
  const recipientLabel = formatInviteRecipientLabel(invite, copy);

  return (
    <li className={className}>
      <button
        type="button"
        className={cn(
          'flex w-full min-h-11 cursor-pointer items-start gap-3 px-4 py-3.5 text-left transition-colors',
          'hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none',
        )}
        onClick={onOpen}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold tracking-tight text-foreground">
              {invite.title}
            </span>
            <FeedbackInviteStatusBadge status={invite.status} />
          </div>

          {context ? (
            <p className="truncate text-sm text-foreground/80">{context}</p>
          ) : null}

          <p className="truncate text-xs text-foreground/80">
            <span className="font-medium text-foreground">{locationName}</span>
            <span className="text-foreground/50"> · </span>
            <span>{recipientLabel}</span>
            <span className="text-foreground/50"> · </span>
            <FeedbackModeLabel mode={invite.mode} />
          </p>
        </div>

        <span className="shrink-0 pt-0.5 text-xs text-foreground/80 tabular-nums">
          {dateLabel}
        </span>
      </button>
    </li>
  );
}
